// src/components/charts/TopRitaseChart.jsx
'use client';

import { useRef, useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import Chart from 'react-apexcharts';
import { useLanguage } from '../../../../utils/i18n';

const PRIMARY = '#485D9A';

export default function TopRitaseChart({ categories = [], series = [], isLoading = false }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(1200);
  const { t } = useLanguage();
  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const isWide = width >= 1000;

  // Deteksi data dan nilai maksimum untuk batas Y-Axis
  const dataValues = series.length > 0 && series[0]?.data ? series[0].data : [];
  const maxValue = dataValues.length > 0 ? Math.max(...dataValues) : 0;

  // Chart Options
  const options = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: true }
    },
    noData: {
      text: isLoading ? ' ' : t('revenue.noDataAvailable', 'No Data Available'),
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: '#888',
        fontSize: '14px'
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: isWide ? '55%' : '65%',
        borderRadius: 4, // Sedikit dikurangi agar tidak terlalu bulat jika bar-nya pendek
        dataLabels: {
          position: 'top'
        }
      }
    },

    dataLabels: {
      enabled: true,
      formatter: (val) => val,
      offsetY: -20,
      style: {
        colors: ['#333'],
        fontSize: '12px',
        fontWeight: 700
      }
    },

    xaxis: {
      categories: categories,
      labels: {
        rotate: isWide ? 0 : -45,
        style: { fontSize: '11px', fontWeight: 600, colors: '#666' }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },

    yaxis: {
      min: 0,
      // Beri ruang ekstra 15% di atas agar angka dataLabels tidak terpotong border chart
      max: maxValue > 0 ? Math.ceil(maxValue + maxValue * 0.15) : 10,
      labels: {
        style: { fontSize: '12px' },
        formatter: (val) => val.toFixed(0) // Pastikan bilangan bulat
      }
    },

    grid: {
      borderColor: '#D8D8D8',
      strokeDashArray: 6,
      yaxis: {
        lines: { show: true }
      }
    },
    colors: [PRIMARY],
    tooltip: {
      y: {
        // Ubah dari Km menjadi Ritase
        formatter: (val) => `${val} ${t('revenue.ritase', 'Ritase')}`
      }
    }
  };

  return (
    <div ref={containerRef}>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0px 6px 20px rgba(0,0,0,0.08)'
        }}
      >
        <CardContent>
          <Typography variant="h4" mb={2} color="text.primary" fontWeight="bold">
            {t('revenue.topRetase', 'Top 10 Ritase')}
          </Typography>

          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300
              }}
            >
              <CircularProgress sx={{ color: PRIMARY, mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t('revenue.calculateTopRetase', 'Calculating Top 10 Ritase...')}
              </Typography>
            </Box>
          ) : (
            <Chart options={options} series={series} type="bar" height={300} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
